/**
 * PromptEditorModal — Ver/Editar o prompt de especialista de um membro do squad
 * (SPEC A2 FR-13; API da A1).
 *
 *   - texto efetivo e a origem legível ("do projeto v3", "do tenant v1", "do catálogo",
 *     "sem prompt");
 *   - checklist das cinco seções ✅/⚠️ em tempo real (validarPrompt.js, paridade com o
 *     Python), contador chars/mínimo e os conflitos com as regras da plataforma;
 *   - "Gerar prompt especialista" (proposta da IA, não salva; "Gerando proposta…"; no 502
 *     a mensagem pt-BR da API e o editor segue livre);
 *   - "Salvar no projeto" (padrão, DA-1) e "Salvar e promover ao tenant" (só com
 *     `agent_catalog:create_edit`; sem ela, desabilitado com o tooltip);
 *   - 422 de segredo lista o tipo e a linha (nunca o valor);
 *   - "Histórico" com as versões do projeto e do tenant e o diff.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Sparkles, Save, History, ArrowUpCircle } from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn } from '@/components/ui';
import {
  usePromptEfetivo, useSalvarPrompt, usePromoverPrompt, usePropostaPrompt,
} from '@/api/agents';
import { validarPrompt, ehPapelGenerico, MINIMO_PADRAO } from '@/lib/validarPrompt';
import { usePodeEditarCatalogo } from '@/lib/permissoes';
import { ChecklistSecoes } from './ChecklistSecoes';
import { HistoricoVersoes } from './HistoricoVersoes';
import { origemLegivel, mensagemDoErro } from './comum';

const TEXTO_DA_REGRA = {
  libera_mock: 'libera mock/dublê',
  edita_teste_congelado: 'manda editar teste congelado',
  ignora_blueprint: 'manda ignorar o blueprint',
  ignora_design_system: 'manda ignorar o Design System',
  sobrescreve_instrucoes: 'manda ignorar as instruções da plataforma',
  marcador_de_injecao: 'marcador de injeção de prompt',
};

const TOOLTIP_SEM_PERMISSAO = 'Requer permissão de edição do catálogo';

function ErroDoSalvar({ error }) {
  if (!error) return null;
  const segredos = Array.isArray(error.problemBody?.segredos) ? error.problemBody.segredos : null;
  return (
    <div role="alert" style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12.5,
      color: C.hot, background: `${C.hot}18`, border: `1px solid ${C.hot}44` }}>
      {segredos && segredos.length ? (
        <>
          <div style={{ fontWeight: 600 }}>
            {error.problemBody?.detail || 'O prompt contém segredo — nada foi gravado.'}
          </div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {segredos.map((s, i) => (
              <li key={`${s.tipo}-${s.linha}-${i}`}>{s.tipo} — linha {s.linha}</li>
            ))}
          </ul>
        </>
      ) : mensagemDoErro(error, 'Não foi possível salvar o prompt. Tente novamente.')}
    </div>
  );
}

/**
 * @param {{
 *   nome: string, projectId: string, rotulo?: string,
 *   gerarAoAbrir?: boolean, onClose: () => void, onSalvo?: (out: object) => void
 * }} props
 */
export function PromptEditorModal({ nome, projectId, rotulo, gerarAoAbrir = false, onClose, onSalvo }) {
  const efetivoQ = usePromptEfetivo(nome, projectId);
  const salvar = useSalvarPrompt();
  const promover = usePromoverPrompt();
  const proposta = usePropostaPrompt();
  const podePromover = usePodeEditarCatalogo();

  const [texto, setTexto] = useState('');
  const [carregado, setCarregado] = useState(false);
  const [origemEdicao, setOrigemEdicao] = useState('manual');
  const [conflitos, setConflitos] = useState([]);
  const [verHistorico, setVerHistorico] = useState(false);
  const [aviso, setAviso] = useState(null);

  const efetivo = efetivoQ.data && typeof efetivoQ.data === 'object' ? efetivoQ.data : null;
  const papelGenerico = ehPapelGenerico(nome);
  const minimo = Number(efetivo?.validacao?.minimo) || MINIMO_PADRAO;

  // Carrega o texto efetivo uma vez (não sobrescreve o que o dono já digitou).
  useEffect(() => {
    if (carregado || efetivoQ.isLoading) return;
    setTexto(typeof efetivo?.texto === 'string' ? efetivo.texto : '');
    setConflitos(Array.isArray(efetivo?.avisos?.conflitos) ? efetivo.avisos.conflitos : []);
    setCarregado(true);
  }, [efetivoQ.isLoading, efetivo, carregado]);

  const validacao = useMemo(
    () => validarPrompt(texto, { papelGenerico, minimo }),
    [texto, papelGenerico, minimo],
  );

  const gerar = () => {
    setAviso(null);
    proposta.mutate({ nome, projectId }, {
      onSuccess: (out) => {
        setTexto(typeof out?.texto === 'string' ? out.texto : '');
        setConflitos(Array.isArray(out?.avisos?.conflitos) ? out.avisos.conflitos : []);
        setOrigemEdicao('proposta_ia');
      },
    });
  };

  // "Gerar prompt especialista" a partir do painel: dispara assim que o editor abre.
  useEffect(() => {
    if (gerarAoAbrir && carregado && !proposta.isPending && !proposta.data && !proposta.isError) gerar();
  }, [gerarAoAbrir, carregado]);  // eslint-disable-line react-hooks/exhaustive-deps

  const salvarNoProjeto = (depois) => {
    setAviso(null);
    promover.reset();
    salvar.mutate({ nome, texto, scope: 'project', projectId, origem: origemEdicao }, {
      onSuccess: (out) => {
        setConflitos(Array.isArray(out?.avisos?.conflitos) ? out.avisos.conflitos : []);
        setAviso(out?.sem_mudanca
          ? 'Sem mudança — o texto é igual à versão atual do projeto.'
          : `Versão v${out?.version ?? '?'} salva no projeto.`);
        onSalvo?.(out);
        if (depois) depois(out);
      },
    });
  };

  const salvarEPromover = () => salvarNoProjeto((out) => {
    if (!out?.version_id) return;
    promover.mutate({ nome, versionId: out.version_id, projectId }, {
      onSuccess: (p) => {
        setAviso(`Salvo no projeto e promovido ao tenant (v${p?.version ?? '?'}).`);
        onSalvo?.(p);
      },
    });
  });

  const ocupado = salvar.isPending || promover.isPending;
  const origemTxt = origemLegivel(efetivo?.origem, efetivo?.version);

  return (
    <div role="dialog" aria-modal="true" aria-label={`Prompt de especialista — ${nome}`}
      style={{ position: 'fixed', inset: 0, zIndex: 220, display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: 'rgba(12,12,12,0.85)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: C.blackElev, border: `1px solid ${C.blackBorder}`, borderRadius: 12,
          padding: 24, width: '100%', maxWidth: 820, maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label="Fechar editor de prompt"
          style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none',
            color: C.neutralMuted, cursor: 'pointer', padding: 4 }}>
          <X size={16} />
        </button>

        <div style={{ fontSize: 18, fontWeight: 700, color: C.neutral, marginBottom: 4 }}>
          Prompt de especialista · {rotulo || nome}
        </div>
        <div style={{ fontSize: 12.5, color: C.neutralDim, marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace' }}>{nome}</span>
          <span data-testid="origem-prompt">Origem: {efetivoQ.isLoading ? 'carregando…' : origemTxt}</span>
          {papelGenerico && <span style={{ color: C.neutralMuted }}>papel genérico — a stack vem do blueprint</span>}
        </div>

        {efetivoQ.isError && (
          <div style={{ fontSize: 12.5, color: C.hot, marginBottom: 10 }}>
            Não foi possível carregar o prompt atual. Você ainda pode escrever e salvar.
          </div>
        )}

        {origemEdicao === 'proposta_ia' && (
          <div data-testid="marca-proposta-ia"
            style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12.5, color: C.warn,
              background: `${C.warn}14`, border: `1px solid ${C.warn}55` }}>
            proposta da IA — revise antes de salvar
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 14 }}>
          <textarea aria-label="Texto do prompt" value={texto} rows={18}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={'## Papel e escopo\n…\n## Stack e convenções\n…\n## Critérios de qualidade\n…\n## Proibido\n…\n## Formato de saída\n…'}
            style={{ width: '100%', boxSizing: 'border-box', background: C.black, color: C.neutral,
              border: `1px solid ${C.blackBorder}`, borderRadius: 8, padding: 12, fontSize: 12.5,
              fontFamily: 'monospace', lineHeight: 1.5, resize: 'vertical' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ChecklistSecoes validacao={validacao} />
            {conflitos.length > 0 && (
              <div role="group" aria-label="Conflitos com as regras"
                style={{ padding: 10, borderRadius: 8, background: `${C.warn}10`, border: `1px solid ${C.warn}55` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.warn, marginBottom: 6 }}>
                  ⚠️ CONFLITO COM REGRAS ({conflitos.length})
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: C.neutralDim }}>
                  {conflitos.map((c, i) => (
                    <li key={`${c.regra}-${i}`}>
                      {TEXTO_DA_REGRA[c.regra] ?? c.regra}
                      {c.linha ? ` (linha ${c.linha})` : ''}
                      {c.trecho ? `: “${c.trecho}”` : ''}
                    </li>
                  ))}
                </ul>
                <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 6 }}>
                  Só avisa: as regras da plataforma prevalecem na montagem do prompt.
                </div>
              </div>
            )}
          </div>
        </div>

        {proposta.isError && (
          <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: C.hot }}>
            {mensagemDoErro(proposta.error,
              'Não foi possível gerar a proposta agora. Você pode escrever o prompt à mão e salvar.')}
          </div>
        )}
        <ErroDoSalvar error={salvar.error} />
        {promover.isError && (
          <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: C.hot }}>
            {mensagemDoErro(promover.error, 'Salvo no projeto, mas não foi possível promover ao tenant.')}
          </div>
        )}
        {aviso && (
          <div role="status" style={{ marginTop: 10, fontSize: 12.5, color: C.cyber }}>{aviso}</div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' }}>
          <Btn variant="outline" size="sm" icon={Sparkles} onClick={gerar} disabled={proposta.isPending}>
            {proposta.isPending ? 'Gerando proposta…' : 'Gerar prompt especialista'}
          </Btn>
          <Btn size="sm" icon={Save} onClick={() => salvarNoProjeto()} disabled={ocupado || !projectId}>
            {salvar.isPending ? 'Salvando…' : 'Salvar no projeto'}
          </Btn>
          <span title={podePromover ? 'Salva no projeto e promove a versão ao tenant' : TOOLTIP_SEM_PERMISSAO}>
            <button type="button" onClick={salvarEPromover} disabled={!podePromover || ocupado || !projectId}
              title={podePromover ? undefined : TOOLTIP_SEM_PERMISSAO}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13,
                borderRadius: 6, background: 'transparent', color: C.neutral, border: `1px solid ${C.blackBorder}`,
                opacity: !podePromover || ocupado ? 0.4 : 1, cursor: !podePromover || ocupado ? 'not-allowed' : 'pointer' }}>
              <ArrowUpCircle size={14} /> Salvar e promover ao tenant
            </button>
          </span>
          <Btn variant="ghost" size="sm" icon={History} onClick={() => setVerHistorico((v) => !v)}>
            {verHistorico ? 'Ocultar histórico' : 'Histórico'}
          </Btn>
          <span style={{ marginLeft: 'auto' }}>
            <Btn variant="ghost" size="sm" onClick={onClose}>Fechar</Btn>
          </span>
        </div>

        {verHistorico && (
          <div style={{ marginTop: 12 }}>
            <HistoricoVersoes nome={nome} projectId={projectId} escopos={['project', 'tenant']} />
          </div>
        )}
      </div>
    </div>
  );
}
